import { useForm } from '@tanstack/react-form';
import { Alert, Button, Input, Text } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../../context/page-context';
import { TwoFactorError } from '../errors';
import { useConfirmTwoFactor } from '../hooks/use-confirm-two-factor';
import type { PasswordConfirmationGuard } from '../hooks/use-password-confirmation-guard';
import { useTwoFactorQrCode } from '../hooks/use-two-factor-qr-code';
import { type ConfirmTwoFactorCodeFormState, confirmTwoFactorCodeFormSchema } from '../schemas';

function QrCodeImage({ svg }: { svg: string }) {
  return (
    <div className="w-fit rounded-lg border border-gray-200 p-2">
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: first-party BaconQrCode SVG from our own API, not user input */}
      <span dangerouslySetInnerHTML={{ __html: svg }} />
    </div>
  );
}

export function EnableTwoFactorFlow({
  guard,
  onConfirmed,
  onCancel,
}: {
  /** Shared with the parent card so there is a single confirm-password modal for the page. */
  guard: PasswordConfirmationGuard;
  onConfirmed: () => void;
  onCancel: () => void;
}) {
  const { notificationHandler } = useHandlers();
  const qrCode = useTwoFactorQrCode(true);
  const confirmTwoFactor = useConfirmTwoFactor();

  // The QR endpoint answers `[]` (not `{svg, url}`) when the user has no secret, so a 200 is
  // not on its own proof that there is something to render.
  const qrCodeData = qrCode.data?.svg ? qrCode.data : undefined;
  const qrCodeFailed = qrCode.isError || (qrCode.isSuccess && !qrCodeData);

  function retryQrCode() {
    void qrCode.refetch().then((result) => {
      // A 423 here means the confirmation window lapsed since the enable call; confirm and retry.
      if (result.isError) {
        guard.retryIfRequired(result.error, () => void qrCode.refetch());
      }
    });
  }

  const form = useForm({
    defaultValues: { code: '' } satisfies ConfirmTwoFactorCodeFormState,
    validators: {
      onSubmit: confirmTwoFactorCodeFormSchema,
    },
    onSubmit: ({ value }) => {
      const payload = confirmTwoFactorCodeFormSchema.parse(value);

      confirmTwoFactor.mutate(payload, {
        onSuccess: onConfirmed,
        onError: (error) => {
          if (error instanceof TwoFactorError && error.id === 'invalid_code') {
            form.setFieldMeta('code', (meta) => ({
              ...meta,
              errorMap: {
                ...meta.errorMap,
                onSubmit: {
                  message: t(
                    'features:account.components.enable_two_factor_flow.errors.code_incorrect',
                  ),
                },
              },
            }));

            return;
          }

          // This route also carries `password.confirm`, and confirming a code is the step the
          // user is most likely to be slow at - so a 423 here is expected, not exceptional.
          if (guard.retryIfRequired(error, () => form.handleSubmit())) {
            return;
          }

          notificationHandler.error(
            t('features:account.components.enable_two_factor_flow.errors.confirm_failed'),
          );
        },
      });
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();

        // Clear errors so we can re-validate
        form.setErrorMap({ onSubmit: { fields: {} } });

        form.handleSubmit();
      }}
    >
      <div className="flex flex-col gap-4">
        <Text variant="secondary">
          {t('features:account.components.enable_two_factor_flow.description')}
        </Text>

        {qrCodeFailed && (
          <Alert headingLevel="span" variant="error">
            <div className="flex flex-col items-start gap-2">
              {t('features:account.components.enable_two_factor_flow.qr_code_failed')}
              <Button
                variant="secondary"
                onClick={retryQrCode}
                loading={qrCode.isFetching}
                type="button"
              >
                {t('features:account.components.enable_two_factor_flow.qr_code_retry')}
              </Button>
            </div>
          </Alert>
        )}

        {qrCodeData && <QrCodeImage svg={qrCodeData.svg} />}

        {qrCodeData && (
          <Text variant="secondary">
            {t('features:account.components.enable_two_factor_flow.url_fallback', {
              url: qrCodeData.url,
            })}
          </Text>
        )}

        <form.Field name="code">
          {(field) => (
            <Input
              label={t('features:account.components.enable_two_factor_flow.code_label')}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={field.state.value}
              onChange={field.handleChange}
              error={field.state.meta.errors[0]?.message}
            />
          )}
        </form.Field>

        <div className="flex gap-2">
          <Button type="submit" loading={confirmTwoFactor.isPending}>
            {t('features:account.components.enable_two_factor_flow.action')}
          </Button>
          <Button
            variant="secondary"
            type="button"
            onClick={onCancel}
            disabled={confirmTwoFactor.isPending}
          >
            {t('features:account.components.enable_two_factor_flow.cancel')}
          </Button>
        </div>
      </div>
    </form>
  );
}
